/**
 * Pillar G — typed guardrail error banner.
 *
 * `runInputGuardrails` (server-side) blocks chat.send with a typed
 * `GuardrailFinding`. The dispatcher writes the finding into
 * `errorShape.details` and `parseStructuredChatError` lifts it onto
 * `ChatErrorDisplay.guardrail`. This module renders that finding as a
 * `.callout.danger` with the rule, limit, current size, and — for the
 * length cases — a Truncate-and-retry affordance that previews the first
 * N / last N chars and resends via `onTruncateRetry`.
 *
 * The banner never replaces the user-visible draft; it surfaces the
 * affordance and the host app decides whether to rewind + resend.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { ChatGuardrailFinding } from "./chat-errors.ts";

export type TruncateDirection = "first" | "last";

export type ChatGuardrailBannerProps = {
  /**
   * Original message text the user attempted to send. Used to build the
   * "first N chars" / "last N chars" previews on demand.
   */
  draftMessage: string;
  /**
   * Server-emitted attachments (typed inline `content` strings). Used by
   * `attachment_max_length` to mark which attachment overshot.
   */
  attachmentCount?: number;
  finding: ChatGuardrailFinding;
  /**
   * Called when the user clicks the truncate-and-retry button. The host
   * decides whether to keep the original draft (we suggest first-N)
   * or swap it for the trimmed string (we suggest last-N).
   * The new text is what should land in the composer / be re-sent.
   */
  onTruncateRetry?: (direction: TruncateDirection, trimmed: string) => void;
  /**
   * Optional dismiss callback — wired when the host renders the banner
   * inline (e.g. inside a transient banner stack). For the persisted
   * assistant-bubble path the banner is part of the bubble so we omit
   * the dismiss control.
   */
  onDismiss?: () => void;
};

/** Number of chars to preview at the head/tail of a truncated message. */
const PREVIEW_HEAD_CHARS = 280;
const PREVIEW_TAIL_CHARS = 280;

/**
 * Truncate `text` to `limit` characters from the requested direction.
 * Returns the trimmed string plus a marker so the user knows content
 * was dropped. Does NOT mutate the input.
 */
export function truncateMessageForGuardrail(
  text: string,
  limit: number,
  direction: TruncateDirection,
): string {
  if (limit <= 0 || text.length <= limit) return text;
  if (direction === "first") {
    return text.slice(0, limit);
  }
  return text.slice(text.length - limit);
}

/**
 * Build the preview text shown above the Truncate buttons. Shows the
 * first `PREVIEW_HEAD_CHARS` and last `PREVIEW_TAIL_CHARS` of the draft
 * with a separator so the user can sanity-check what will be dropped.
 */
export function buildTruncatePreview(text: string): {
  head: string;
  tail: string;
  total: number;
} {
  if (text.length <= PREVIEW_HEAD_CHARS + PREVIEW_TAIL_CHARS) {
    return { head: text, tail: "", total: text.length };
  }
  return {
    head: text.slice(0, PREVIEW_HEAD_CHARS),
    tail: text.slice(text.length - PREVIEW_TAIL_CHARS),
    total: text.length,
  };
}

function ruleLabel(rule: string): string {
  switch (rule) {
    case "max_length":
      return "max length";
    case "min_length":
      return "min length";
    case "attachment_max_length":
      return "attachment too large";
    case "prompt_injection":
      return "prompt injection";
    case "pii":
      return "PII";
    default:
      return rule;
  }
}

function detailLine(props: ChatGuardrailBannerProps): string {
  const f = props.finding;
  const formatNum = (n: number) => n.toLocaleString();
  switch (f.kind) {
    case "max_length": {
      const current = f.currentSize ?? 0;
      const limit = f.limit ?? 0;
      const overshoot = current - limit;
      return (
        `Your message is ${formatNum(current)} chars; the guardrail caps chat.send at ` +
        `${formatNum(limit)} (over by ${formatNum(overshoot)}).`
      );
    }
    case "min_length": {
      const current = f.currentSize ?? 0;
      const limit = f.limit ?? 0;
      return (
        `Your message is ${current} char${current === 1 ? "" : "s"}; ` +
        `the guardrail requires at least ${limit}.`
      );
    }
    case "attachment_max_length": {
      const current = f.currentSize ?? 0;
      const limit = f.limit ?? 0;
      const where =
        f.attachmentIndex !== undefined ? `attachment[${f.attachmentIndex}]` : "attachment";
      const of =
        props.attachmentCount && props.attachmentCount > 1 ? ` of ${props.attachmentCount}` : "";
      return (
        `${where}${of} is ${formatNum(current)} chars; ` +
        `the guardrail caps attachments at ${formatNum(limit)}.`
      );
    }
    case "prompt_injection": {
      return `Matched a high-confidence prompt-injection pattern${f.matchedPattern ? ` (${f.matchedPattern})` : ""}.`;
    }
    case "pii": {
      // matchedCategory is a renderer hint on the PI branch only — the
      // base ChatGuardrailFinding doesn't carry it, but the renderer
      // assumes one; we accept it via a structural check below to keep
      // the type strict.
      const matched = (f as { matchedCategory?: string }).matchedCategory;
      return `Detected PII${matched ? ` (${matched})` : ""}.`;
    }
    default:
      // Unknown / missing kind — fall back to the rule string and any
      // detail the gateway included. Keeps the banner informative when
      // a newer backend ships a rule the renderer doesn't yet know.
      return f.detail ?? f.message ?? f.rule;
  }
}

function truncateSection(props: ChatGuardrailBannerProps): TemplateResult | typeof nothing {
  if (!props.onTruncateRetry) return nothing;
  if (props.finding.kind !== "max_length" && props.finding.kind !== "attachment_max_length") {
    return nothing;
  }
  const limit = props.finding.limit ?? 0;
  const preview = buildTruncatePreview(props.draftMessage);
  const previewBody = preview.tail
    ? html`
        <pre
          class="chat-guardrail-banner__preview"
          aria-label="Truncated message preview"
        ><span class="chat-guardrail-banner__preview-head">${preview.head}</span>
…[${(preview.total - preview.head.length - preview.tail.length).toLocaleString()} chars dropped]…
<span class="chat-guardrail-banner__preview-tail">${preview.tail}</span></pre>
      `
    : html`
        <pre class="chat-guardrail-banner__preview" aria-label="Message preview">
${preview.head}</pre>
      `;
  return html`
    <div class="chat-guardrail-banner__truncate">
      ${previewBody}
      <div class="chat-guardrail-banner__truncate-actions">
        <button
          type="button"
          class="btn btn--sm"
          @click=${() =>
            props.onTruncateRetry?.(
              "first",
              truncateMessageForGuardrail(props.draftMessage, limit, "first"),
            )}
        >
          Send first ${limit.toLocaleString()} chars
        </button>
        <button
          type="button"
          class="btn btn--sm subtle"
          @click=${() =>
            props.onTruncateRetry?.(
              "last",
              truncateMessageForGuardrail(props.draftMessage, limit, "last"),
            )}
        >
          Send last ${limit.toLocaleString()} chars
        </button>
      </div>
    </div>
  `;
}

export function renderChatGuardrailBanner(props: ChatGuardrailBannerProps): TemplateResult {
  const f = props.finding;
  return html`
    <div
      class="callout danger chat-guardrail-banner fleet-chat-workspace__banner"
      role="alert"
      data-guardrail-rule=${f.rule}
    >
      <div class="chat-guardrail-banner__head">
        <span class="chat-guardrail-banner__badge">Guardrail</span>
        <span class="chat-guardrail-banner__rule">${ruleLabel(f.rule)}</span>
      </div>
      <div class="chat-guardrail-banner__title">
        Blocked by chat guardrail: ${ruleLabel(f.rule)}
      </div>
      <p class="chat-guardrail-banner__detail">${detailLine(props)}</p>
      ${truncateSection(props)}
      ${
        props.onDismiss
          ? html`
              <button
                type="button"
                class="btn subtle btn--sm chat-guardrail-banner__dismiss"
                @click=${() => props.onDismiss?.()}
              >
                Dismiss
              </button>
            `
          : nothing
      }
    </div>
  `;
}
